import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, UserPlus, Pencil, Power, ArrowLeft, Crown, Shield, Users as UsersIcon, Mail, KeyRound, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminUsers() {
  const { user: currentUser, loadMemberships } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState(null); // user object
  const [assignUser, setAssignUser] = useState(null); // user object
  const [resetUser, setResetUser] = useState(null); // user object
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (currentUser && currentUser.role !== "club_admin") {
      navigate("/", { replace: true });
    }
  }, [currentUser, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const [u, t] = await Promise.all([
        api.get("/clubs/me/users"),
        api.get("/clubs/me/teams"),
      ]);
      setUsers(u.data);
      setTeams(t.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Laden fehlgeschlagen");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggleActive = async (u) => {
    if (u.id === currentUser?.id) {
      toast.error("Eigenes Konto kann nicht deaktiviert werden");
      return;
    }
    const action = u.active ? "deaktivieren" : "aktivieren";
    if (!window.confirm(`Trainer "${u.name}" ${action}?`)) return;
    try {
      await api.put(`/users/${u.id}`, { active: !u.active });
      toast.success(`Trainer ${u.active ? "deaktiviert" : "aktiviert"}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen");
    }
  };

  const deleteUser = async (u) => {
    if (u.id === currentUser?.id) {
      toast.error("Eigenes Konto kann nicht gelöscht werden");
      return;
    }
    if (!window.confirm(
      `Trainer "${u.name}" wirklich PERMANENT löschen?\n\n` +
      "Diese Aktion kann nicht rückgängig gemacht werden. Mannschafts-Zuweisungen werden " +
      "ebenfalls gelöscht. Spieler-, Trainings- und Spielnoten-Daten bleiben erhalten (gehören zur Mannschaft).",
    )) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`Trainer "${u.name}" gelöscht`);
      await load();
      await loadMemberships();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Löschen fehlgeschlagen");
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-6 max-w-4xl mx-auto" data-testid="admin-users-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/admin")}
            className="text-zinc-400 hover:text-brand-lighter hover:bg-zinc-900" data-testid="au-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" />
          </Button>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-brand-lighter">Verwaltung</div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold uppercase">Benutzer</h1>
          </div>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="bg-brand hover:bg-brand-light text-white rounded-md" data-testid="au-create-btn">
          <UserPlus className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Trainer anlegen</span>
        </Button>
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm">Lädt…</div>
      ) : (
        <div className="space-y-2" data-testid="au-list">
          {users.map((u) => (
            <div key={u.id}
              className={"rounded-md border bg-zinc-900/40 p-4 " + (u.active ? "border-zinc-800" : "border-zinc-800/50 opacity-60")}
              data-testid={`au-user-${u.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <div className={"h-10 w-10 flex items-center justify-center rounded-md shrink-0 " + (u.role === "club_admin" ? "bg-amber-500/20" : "bg-brand/20")}>
                    {u.role === "club_admin"
                      ? <Crown className="h-5 w-5 text-amber-400" />
                      : <Shield className="h-5 w-5 text-brand-lighter" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-zinc-100 truncate">
                      {u.name}
                      {u.id === currentUser?.id && (
                        <span className="ml-2 text-[10px] uppercase tracking-wider text-brand-lighter">(Du)</span>
                      )}
                    </div>
                    <div className="text-xs text-zinc-500 truncate flex items-center gap-1">
                      <Mail className="h-3 w-3" />{u.email}
                    </div>
                    <div className="mt-1.5 text-[11px] uppercase tracking-wider text-zinc-500">
                      {u.role === "club_admin" ? "Vereinsadmin" : "Trainer"}
                      {" · "}{u.active ? "Aktiv" : "Deaktiviert"}
                    </div>
                    {u.memberships.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`au-user-${u.id}-memberships`}>
                        {u.memberships.map((m) => (
                          <span key={m.team_id}
                            className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-md bg-zinc-800 text-zinc-300">
                            {m.team_name}
                            {m.label === "head" && <span className="ml-1 text-amber-400">★</span>}
                            {m.label === "co" && <span className="ml-1 text-brand-lighter">·co</span>}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => setEditUser(u)}
                    className="text-zinc-400 hover:text-brand-lighter hover:bg-zinc-800" data-testid={`au-edit-${u.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  {u.id !== currentUser?.id && (
                    <Button variant="ghost" size="sm" onClick={() => setResetUser(u)}
                      className="text-zinc-400 hover:text-brand-lighter hover:bg-zinc-800" data-testid={`au-reset-${u.id}`}>
                      <KeyRound className="h-4 w-4" />
                    </Button>
                  )}
                  {u.role !== "club_admin" && (
                    <Button variant="ghost" size="sm" onClick={() => setAssignUser(u)}
                      className="text-zinc-400 hover:text-brand-lighter hover:bg-zinc-800" data-testid={`au-assign-${u.id}`}>
                      <UsersIcon className="h-4 w-4" />
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => toggleActive(u)}
                    disabled={u.id === currentUser?.id}
                    className={"hover:bg-zinc-800 " + (u.active ? "text-zinc-400 hover:text-amber-400" : "text-emerald-400 hover:text-emerald-300") + " disabled:opacity-30"}
                    data-testid={`au-toggle-${u.id}`}>
                    <Power className="h-4 w-4" />
                  </Button>
                  {u.id !== currentUser?.id && (
                    <Button variant="ghost" size="sm" onClick={() => deleteUser(u)}
                      className="text-zinc-400 hover:text-red-400 hover:bg-zinc-800"
                      data-testid={`au-delete-${u.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <CreateUserDialog open={createOpen} onOpenChange={setCreateOpen}
        onCreated={async () => { setCreateOpen(false); await load(); }} />

      <EditUserDialog user={editUser} currentUserId={currentUser?.id}
        onClose={() => setEditUser(null)}
        onSaved={async () => { setEditUser(null); await load(); await loadMemberships(); }} />

      <AssignMembershipsDialog user={assignUser} teams={teams}
        onClose={() => setAssignUser(null)}
        onSaved={async () => { setAssignUser(null); await load(); await loadMemberships(); }} />

      <ResetPasswordDialog user={resetUser}
        onClose={() => setResetUser(null)} />
    </div>
  );
}

function CreateUserDialog({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("trainer");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setName(""); setEmail(""); setPassword(""); setRole("trainer"); }
  }, [open]);

  const save = async () => {
    if (!name.trim() || !email.trim() || password.length < 6) {
      toast.error("Name, E-Mail und Passwort (min. 6 Zeichen) erforderlich");
      return;
    }
    setSaving(true);
    try {
      await api.post("/clubs/me/users", {
        name: name.trim(), email: email.trim().toLowerCase(), password, role,
      });
      toast.success(`Trainer "${name}" angelegt`);
      await onCreated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Anlegen fehlgeschlagen");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
        <DialogHeader><DialogTitle>Trainer anlegen</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs text-zinc-400">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
              className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1 h-10 rounded-md"
              data-testid="cu-name" autoFocus />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">E-Mail</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1 h-10 rounded-md" data-testid="cu-email" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Initialpasswort</Label>
            <Input type="text" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Mind. 6 Zeichen — Trainer kann später ändern"
              className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1 h-10 rounded-md" data-testid="cu-password" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Rolle</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1 h-10 rounded-md" data-testid="cu-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                <SelectItem value="trainer">Trainer</SelectItem>
                <SelectItem value="club_admin">Vereinsadmin</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-[11px] text-zinc-500 mt-1">
              {role === "club_admin"
                ? "Sieht automatisch alle Mannschaften des Vereins."
                : "Bekommt Zugriff nur über Mannschafts-Zuweisung."}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
            className="bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-900 rounded-md">Abbrechen</Button>
          <Button onClick={save} disabled={saving}
            className="bg-brand hover:bg-brand-light text-white rounded-md" data-testid="cu-save">
            {saving ? "Speichern…" : "Anlegen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditUserDialog({ user, currentUserId, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [role, setRole] = useState("trainer");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) { setName(user.name); setRole(user.role); }
  }, [user]);

  if (!user) return null;
  const isSelf = user.id === currentUserId;

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/users/${user.id}`, { name: name.trim(), role });
      toast.success("Trainer aktualisiert");
      await onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
        <DialogHeader><DialogTitle>Trainer bearbeiten</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs text-zinc-400">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
              className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1 h-10 rounded-md" data-testid="eu-name" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Rolle</Label>
            <Select value={role} onValueChange={setRole} disabled={isSelf}>
              <SelectTrigger className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1 h-10 rounded-md" data-testid="eu-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                <SelectItem value="trainer">Trainer</SelectItem>
                <SelectItem value="club_admin">Vereinsadmin</SelectItem>
              </SelectContent>
            </Select>
            {isSelf && (
              <div className="text-[11px] text-zinc-500 mt-1">
                Eigene Rolle kann nicht geändert werden.
              </div>
            )}
          </div>
          <div className="text-xs text-zinc-500">
            E-Mail: {user.email}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}
            className="bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-900 rounded-md">Abbrechen</Button>
          <Button onClick={save} disabled={saving}
            className="bg-brand hover:bg-brand-light text-white rounded-md" data-testid="eu-save">
            {saving ? "Speichern…" : "Speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssignMembershipsDialog({ user, teams, onClose, onSaved }) {
  const [selected, setSelected] = useState({}); // {team_id: 'head'|'co'}
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      const init = {};
      user.memberships.forEach((m) => { init[m.team_id] = m.label || "co"; });
      setSelected(init);
    }
  }, [user]);

  if (!user) return null;
  const activeTeams = teams.filter((t) => t.active);

  const toggle = (teamId) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[teamId]) delete next[teamId];
      else next[teamId] = "co";
      return next;
    });
  };
  const setLabel = (teamId, label) => {
    setSelected((prev) => ({ ...prev, [teamId]: label }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const memberships = Object.entries(selected).map(([team_id, label]) => ({ team_id, label }));
      await api.put(`/users/${user.id}/memberships`, { memberships });
      toast.success("Mannschafts-Zuweisung aktualisiert");
      await onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-lg">
        <DialogHeader>
          <DialogTitle>Mannschaften zuweisen — {user.name}</DialogTitle>
        </DialogHeader>
        <div className="py-2 max-h-80 overflow-y-auto space-y-2" data-testid="am-list">
          {activeTeams.length === 0 ? (
            <div className="text-sm text-zinc-500 italic">Keine aktiven Mannschaften.</div>
          ) : activeTeams.map((t) => {
            const checked = !!selected[t.id];
            return (
              <div key={t.id}
                className="rounded-md border border-zinc-800 bg-zinc-900/40 p-3 flex items-center gap-3"
                data-testid={`am-team-${t.id}`}>
                <Checkbox checked={checked} onCheckedChange={() => toggle(t.id)}
                  className="border-zinc-600 data-[state=checked]:bg-brand data-[state=checked]:border-brand"
                  data-testid={`am-check-${t.id}`} />
                <div className="flex-1">
                  <div className="text-sm font-medium text-zinc-100">{t.name}</div>
                </div>
                {checked && (
                  <Select value={selected[t.id]} onValueChange={(v) => setLabel(t.id, v)}>
                    <SelectTrigger className="bg-zinc-900 border-zinc-800 text-zinc-100 h-9 w-32 rounded-md"
                      data-testid={`am-label-${t.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                      <SelectItem value="head">Cheftrainer</SelectItem>
                      <SelectItem value="co">Co-Trainer</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}
            className="bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-900 rounded-md">Abbrechen</Button>
          <Button onClick={save} disabled={saving}
            className="bg-brand hover:bg-brand-light text-white rounded-md" data-testid="am-save">
            {saving ? "Speichern…" : "Speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ user, onClose }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) { setPassword(""); setConfirm(""); }
  }, [user]);

  if (!user) return null;

  const generate = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
    let p = "";
    for (let i = 0; i < 10; i++) p += chars[Math.floor(Math.random() * chars.length)];
    setPassword(p);
    setConfirm(p);
  };

  const save = async () => {
    if (password.length < 6) {
      toast.error("Passwort muss mindestens 6 Zeichen lang sein");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwörter stimmen nicht überein");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/users/${user.id}/reset-password`, { password });
      toast.success(`Passwort für ${user.name} zurückgesetzt`);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Zurücksetzen fehlgeschlagen");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
        <DialogHeader>
          <DialogTitle>Passwort zurücksetzen — {user.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="text-xs text-zinc-500">
            E-Mail: {user.email}
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Neues Passwort</Label>
            <div className="flex gap-2 mt-1">
              <Input
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mind. 6 Zeichen"
                className="bg-zinc-900 border-zinc-800 text-zinc-100 h-10 rounded-md flex-1"
                data-testid="rp-password"
                autoFocus
              />
              <Button
                type="button"
                variant="outline"
                onClick={generate}
                className="bg-transparent border-zinc-700 text-brand-lighter hover:bg-zinc-900 rounded-md h-10"
                data-testid="rp-generate"
              >
                Generieren
              </Button>
            </div>
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Wiederholen</Label>
            <Input
              type="text"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1 h-10 rounded-md"
              data-testid="rp-confirm"
            />
          </div>
          <div className="rounded-md border border-amber-900/40 bg-amber-900/10 p-3 text-xs text-amber-200">
            Teile das neue Passwort dem Trainer persönlich oder per sicherem Kanal mit.
            Der Trainer sollte es nach dem ersten Login ändern.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}
            className="bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-900 rounded-md">
            Abbrechen
          </Button>
          <Button onClick={save} disabled={saving}
            className="bg-brand hover:bg-brand-light text-white rounded-md" data-testid="rp-save">
            {saving ? "Speichern…" : "Zurücksetzen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

